<script setup lang="ts">
  // Staff balance top-up: plan tile (or free amount ≥ minTopUp) + tender.
  // Money collection goes through POST /club/topup — NOT the raw payments
  // endpoint — so plan/bonus matching, max-balance and idempotency are all
  // enforced server-side. Emits 'topped-up' with the refreshed member.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { v4 as uuidv4 } from 'uuid';
  import { ClubService } from '@/api/services/club.service';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import { AlertType } from '@/interfaces/IAlert';
  import { IClubMember } from '@/interfaces/IClubMember';
  import { IClubProgram, IClubProgramPublic } from '@/interfaces/IClubProgram';
  import { Globals } from '@/modules/Globals';
  import { roundPrice } from '@/utils';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { getClubCurrencyDefaults } from '@/domain/club/club-currency-defaults';
  import { topupBonusForAmount } from '@/domain/club/club-program-math';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  const MODAL_NAME = 'v2-club-topup';

  /** Tenders a cashier can actually collect for a top-up. */
  const TOPUP_METHODS = [
    PaymentMethod.Cash,
    PaymentMethod.Card,
    PaymentMethod.BankTransfer,
    PaymentMethod.QR,
  ];

  const props = defineProps<{
    member: IClubMember;
    program: IClubProgram | IClubProgramPublic;
  }>();

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');
  const defaults = getClubCurrencyDefaults(
    restaurantStore.currency?.value ?? null
  );

  const plans = computed(() => props.program.topUpPlans ?? []);
  const minTopUp = computed(() => +props.program.minTopUp || 0);

  const paymentMethods = Globals.shortPaymentMethods.filter((method) =>
    TOPUP_METHODS.includes(method.id)
  );

  // null = free amount; otherwise an index into plans.
  const planIndex = ref<number | null>(plans.value.length ? 0 : null);
  const freeAmountRaw = ref('');
  const paymentMethod = ref<PaymentMethod | null>(null);
  const saving = ref(false);

  // Stable while the inputs are unchanged, so retrying the same request
  // replays instead of charging twice. Rotated when the cashier edits the
  // charge, so a replay can never silently answer for a DIFFERENT amount
  // (e.g. after a timed-out request that actually succeeded).
  const idempotencyKey = ref(uuidv4());
  watch([planIndex, freeAmountRaw, paymentMethod], () => {
    idempotencyKey.value = uuidv4();
  });

  const principal = computed(() => {
    if (planIndex.value != null) {
      return +(plans.value[planIndex.value]?.amount ?? 0);
    }
    const num = Number(freeAmountRaw.value);
    return isNaN(num) ? 0 : num;
  });

  // Threshold semantics mirroring the server (resolveTopupBonus): a custom
  // amount at/over a tier earns that tier's bonus, plan selected or not.
  const bonus = computed(() =>
    topupBonusForAmount(plans.value, principal.value)
  );

  const newBalance = computed(() =>
    roundPrice(props.member.cashTotal + principal.value + bonus.value)
  );

  const freeAmountError = computed<string | null>(() => {
    if (planIndex.value != null || !freeAmountRaw.value) return null;
    if (!(principal.value > 0)) return i18nT('club.topup.errorAmount');
    if (minTopUp.value > 0 && principal.value < minTopUp.value) {
      return i18nT('club.topup.errorMin', {
        symbol: symbol.value,
        min: minTopUp.value.toLocaleString('es-AR'),
      });
    }
    return null;
  });

  const canConfirm = computed(
    () =>
      principal.value > 0 &&
      !freeAmountError.value &&
      paymentMethod.value != null
  );

  function selectPlan(index: number | null) {
    planIndex.value = index;
    if (index != null) freeAmountRaw.value = '';
  }

  async function confirm() {
    if (!canConfirm.value || saving.value) return;

    // Same guard as ModalPay: store tenders in a caja-managed branch must
    // land in a caja (the server rejects otherwise, this just fails sooner).
    if (
      getEffectiveTier(restaurantStore.restaurant) > 0 &&
      restaurantStore.selectedBranch?.settingUseCajas &&
      !restaurantStore.caja
    ) {
      ui.showToast(AlertType.Error, i18nT('club.topup.errorNoCaja'));
      return;
    }

    try {
      saving.value = true;

      await ClubService.topup({
        restaurantId: restaurantStore.restaurant!.id,
        branchId: restaurantStore.selectedBranch!.id,
        memberId: props.member.id,
        amount: principal.value,
        planIndex: planIndex.value,
        paymentMethod: paymentMethod.value!,
        cajaId: restaurantStore.caja?.id ?? null,
        idempotencyKey: idempotencyKey.value,
      });

      const updated = await ClubService.getMember(props.member.id);

      ui.showToast(
        AlertType.Success,
        bonus.value > 0
          ? i18nT('club.topup.savedToastBonus', {
              symbol: symbol.value,
              balance: updated.cashTotal.toLocaleString('es-AR'),
              bonus: bonus.value.toLocaleString('es-AR'),
            })
          : i18nT('club.topup.savedToast', {
              symbol: symbol.value,
              balance: updated.cashTotal.toLocaleString('es-AR'),
            })
      );
      proxy.$parent?.$emit('topped-up', updated);
      // The modal unmounts here; don't touch reactive state afterwards.
      modals.hide(MODAL_NAME);
    } catch (e) {
      ui.serverError(e, i18nT('club.topup.saveError'));
      saving.value = false;
    }
  }

  function cancel() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="v2-modal-club-topup">
    <div class="modal-header">
      <span class="modal-title">{{ $t('club.topup.title') }}</span>
      <span class="modal-subtitle">{{ member.name }}</span>
    </div>

    <div class="modal-content">
      <!-- Amount: plans + free amount -->
      <div class="topup-field">
        <label class="topup-label">{{ $t('club.topup.amountLabel') }}</label>
        <div class="plan-grid">
          <button
            v-for="(plan, index) in plans"
            :key="index"
            type="button"
            class="plan-tile"
            :class="{ selected: planIndex === index }"
            :disabled="saving"
            @click="selectPlan(index)"
          >
            <span class="plan-amount">
              {{ symbol }}{{ plan.amount.toLocaleString('es-AR') }}
            </span>
            <span v-if="plan.bonus > 0" class="plan-bonus">
              {{
                $t('club.topup.bonusTag', {
                  symbol,
                  bonus: plan.bonus.toLocaleString('es-AR'),
                })
              }}
            </span>
          </button>
          <button
            type="button"
            class="plan-tile"
            :class="{ selected: planIndex === null }"
            :disabled="saving"
            @click="selectPlan(null)"
          >
            <span class="plan-amount free">
              {{ $t('club.topup.freeAmount') }}
            </span>
          </button>
        </div>

        <template v-if="planIndex === null">
          <PosUiInput
            :value="freeAmountRaw"
            type="number"
            inputmode="decimal"
            :prefix="symbol"
            :placeholder="String(defaults.earnUnitAmount)"
            :disabled="saving"
            @input="freeAmountRaw = $event"
          />
          <span v-if="freeAmountError" class="topup-error">
            {{ freeAmountError }}
          </span>
          <span v-else-if="minTopUp > 0" class="topup-hint">
            {{
              $t('club.topup.minHint', {
                symbol,
                min: minTopUp.toLocaleString('es-AR'),
              })
            }}
          </span>
        </template>
      </div>

      <!-- Tender -->
      <div class="topup-field">
        <label class="topup-label">{{ $t('club.topup.methodLabel') }}</label>
        <div class="method-grid">
          <button
            v-for="method in paymentMethods"
            :key="method.id"
            type="button"
            class="method-tile"
            :class="{ selected: paymentMethod === method.id }"
            :disabled="saving"
            @click="paymentMethod = method.id"
          >
            {{ method.title }}
          </button>
        </div>
      </div>

      <!-- Preview -->
      <div v-if="principal > 0 && !freeAmountError" class="topup-preview">
        {{
          $t('club.topup.preview', {
            symbol,
            balance: newBalance.toLocaleString('es-AR'),
          })
        }}
        <template v-if="bonus > 0">
          ({{
            $t('club.topup.bonusTag', {
              symbol,
              bonus: bonus.toLocaleString('es-AR'),
            })
          }})
        </template>
      </div>
    </div>

    <div class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        :loading="saving"
        :disabled="!canConfirm"
        @click="confirm"
      >
        {{
          $t('club.topup.confirm', {
            symbol,
            amount: principal > 0 ? principal.toLocaleString('es-AR') : 0,
          })
        }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-club-topup {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-white;

    .modal-header {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;

      .modal-title {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-5 tw-py-4;

      .topup-field {
        @apply tw-flex tw-flex-col tw-gap-1.5;

        .topup-label {
          @apply tw-text-sm tw-font-medium tw-text-neutral-700;
        }

        .topup-hint {
          @apply tw-text-xs tw-text-neutral-400;
        }

        .topup-error {
          @apply tw-text-xs tw-font-medium tw-text-red-600;
        }
      }

      .plan-grid {
        @apply tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-3;

        .plan-tile {
          @apply tw-flex tw-min-h-16 tw-flex-col tw-items-center tw-justify-center tw-gap-0.5 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2 tw-py-2 tw-transition-colors hover:tw-bg-neutral-50;

          &.selected {
            @apply tw-border-orange-500 tw-bg-orange-50;
          }

          .plan-amount {
            @apply tw-text-base tw-font-bold tw-tabular-nums tw-text-neutral-900;

            &.free {
              @apply tw-text-sm tw-font-semibold tw-text-neutral-600;
            }
          }

          .plan-bonus {
            @apply tw-text-xs tw-font-medium tw-text-emerald-700;
          }
        }
      }

      .method-grid {
        @apply tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4;

        .method-tile {
          @apply tw-flex tw-min-h-12 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2 tw-text-sm tw-font-medium tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

          &.selected {
            @apply tw-border-orange-500 tw-bg-orange-50 tw-text-neutral-900;
          }
        }
      }

      .topup-preview {
        @apply tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-tabular-nums tw-text-neutral-700;
      }
    }

    .modal-actions {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3;
    }
  }
</style>
